import { CompilationPlayerTrack, RadioStationTrack, StreamingPlayerError } from '@heardis/stream-contracts';
import { Buffer } from 'buffer';
import { PlayerTrack } from '../contexts/PlayerContext';
import { Logger } from './logger';

const logger = new Logger('Tracks');

export async function getCompilationPlayerTrackById(apiEndpoint: string, trackId: string): Promise<CompilationPlayerTrack | null> {
  try {
    const trackResponse = await fetch(`${apiEndpoint}/${trackId}`);
    const track: CompilationPlayerTrack = await trackResponse.json();

    return track;
  } catch (error) {
    logger.error(`Error ${StreamingPlayerError.FAILED_TO_FETCH_TRACK} failed to fetch track ${trackId}:`, error);
    /** @TODO error must been returned */
    // setTrackError(trackIndex, StreamingPlayerError.FAILED_TO_FETCH_TRACK);
    return null;
  }
}

type FetchTracksError = StreamingPlayerError | undefined;
interface FetchTracks<T> {
  tracks: T[];
  status: {
    loading: boolean;
    error: FetchTracksError;
  };
}

export const preloadPlayerTrack = (track: RadioStationTrack | CompilationPlayerTrack): PlayerTrack => {
  logger.debug('preload', { trackId: track.id });
  const audio = new Audio(track.trackUri);
  audio.preload = 'auto';
  return { track, audio };
};

export async function fetchTracks<T>(apiEndpoint, cache, authEnabled, user): Promise<FetchTracks<T>> {
  const cacheIsDisabled = !cache;
  let previewQuery = '';
  let error: FetchTracksError;
  let loading = true;
  let tracks: T[] = [];

  if (cacheIsDisabled) {
    const randomPreviewId = Math.random().toString(36).slice(2);
    previewQuery = `?r=${randomPreviewId}`;
  }

  try {
    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    const encodedTimeZone = Buffer.from(timeZone, 'utf-8').toString('base64');
    let fetchedTracksPromise;

    if (authEnabled) {
      fetchedTracksPromise = await fetch(`${apiEndpoint}${previewQuery}`, {
        headers: new Headers({
          Authorization: `Bearer ${user.getSignInUserSession()?.getAccessToken().getJwtToken()}`,
          'hdis-lid': encodedTimeZone,
        }),
      });
    } else {
      fetchedTracksPromise = await fetch(`${apiEndpoint}${previewQuery}`, {
        headers: new Headers({
          'hdis-lid': encodedTimeZone,
        }),
      });
    }

    const fetchedTracks = await fetchedTracksPromise.json();

    if (fetchedTracks.length) {
      tracks = fetchedTracks;
    } else {
      if (fetchedTracksPromise.status === 401 || fetchedTracksPromise.status === 200) {
        // We expect the player doesn't have the latest playlists (radio station) or no tracks assigned (compilation)
        logger.error('no tracks provided:', StreamingPlayerError.NO_TRACKS_PROVIDED);
        error = StreamingPlayerError.NO_TRACKS_PROVIDED;
      }
      if (fetchedTracksPromise.status === 500) {
        // We expect the lambda to be cold starting
        logger.error('missing api endpoint', StreamingPlayerError.MISSING_API_ENDPOINT);
        error = StreamingPlayerError.MISSING_API_ENDPOINT;
      }
    }
  } catch (err) {
    // We expect the backend to be offline (res.status = 200 but it returns not found)
    logger.error('error fetching tracks:', err);
    error = StreamingPlayerError.FAILED_TO_FETCH_TRACKS;
  } finally {
    loading = false;
  }

  return { tracks, status: { loading, error } };
}
